import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListInvoicesResult,
  type ListUsageMetersResult,
  type RecognitionByMonthResult,
  type RunInvoicingResult,
} from '@whinerp/client';
import { Button, ErrorState, EmptyState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-75 Invoices (G-129).
 *
 * Two things on this screen are there to stop a specific mistake.
 *
 * Drafts sort above issued invoices and say why they are drafts. A draft here
 * is not somebody's unfinished work - it is an invoice the generator refused
 * to issue because it could not work out the tax, and it is money not asked
 * for until somebody looks.
 *
 * "Run billing now" reports what it skipped as loudly as what it issued. A
 * run that says "3 issued" and nothing else hides the subscription that has
 * been failing quietly for two months.
 *
 * The recognition strip (G-137) puts earned beside deferred, because an
 * invoice is not revenue: 1,200 billed in January for a year is 100 earned
 * and 1,100 still owed as service, and a screen showing only the billing
 * overstates the month twelvefold.
 *
 * The meters list (G-136) shows each meter's aggregation in words rather than
 * as a code, because it is the one setting here that produces a bill an order
 * of magnitude wrong while looking entirely ordinary: a daily seat count
 * summed bills a ten-seat customer for three hundred seats.
 */

type Invoice = ListInvoicesResult['invoices'][number];

const MONEY = { draft: 'text-ink-meta', issued: 'text-ink', paid: 'text-success-text', void: 'text-ink-meta' } as const;

function InvoicesScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [run, setRun] = useState<RunInvoicingResult | null>(null);

  const invoices = useQuery<ListInvoicesResult, ApiError>({
    queryKey: ['invoices'],
    queryFn: () => api.listInvoices({}),
  });

  const year = new Date().getUTCFullYear();
  const recognition = useQuery<RecognitionByMonthResult, ApiError>({
    queryKey: ['revenue-recognition', year],
    queryFn: () =>
      api.recognitionByMonth({ query: { from: `${String(year)}-01-01`, to: `${String(year)}-12-31` } }),
  });

  const meters = useQuery<ListUsageMetersResult, ApiError>({
    queryKey: ['usage-meters'],
    queryFn: () => api.listUsageMeters({}),
  });

  const billNow = useMutation<RunInvoicingResult, ApiError>({
    mutationFn: () => api.runInvoicing({ body: {} }),
    onSuccess: (result) => {
      setRun(result);
      // Both: billing changes what was invoiced AND what is now scheduled to
      // be earned, and refreshing only the table leaves the revenue strip
      // showing the position before the run.
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['revenue-recognition'] });
    },
  });

  if (invoices.isPending) return <Loading />;
  if (invoices.isError) {
    return (
      <div data-component="Invoices">
        <ErrorState title={t('state.error')} body={errorText(invoices.error)} onRetry={() => void invoices.refetch()} />
      </div>
    );
  }

  // Drafts first: they are the ones nobody has been asked to pay.
  const rows = [...invoices.data.invoices].sort((a, b) => Number(b.status === 'draft') - Number(a.status === 'draft'));
  const drafts = rows.filter((invoice) => invoice.status === 'draft').length;

  return (
    <div data-component="Invoices" className="flex w-full flex-col gap-4">
      <PageHeading>{t('invoices.title')}</PageHeading>
      <Link to="/revenue/dunning" data-open-dunning className="self-start text-dense text-bronze underline">{t('dunning.title')}</Link>
      <p className="text-body text-ink-meta">{t('invoices.help')}</p>

      {can('revenue.manage') ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button intent="primary" size="small" data-invoices-run onClick={() => billNow.mutate()} disabled={billNow.isPending}>
            {t('invoices.run')}
          </Button>
          {billNow.error ? <span className="text-dense text-danger-text">{errorText(billNow.error)}</span> : null}
          {run ? (
            <span className="text-dense text-ink-meta" data-invoices-run-result>
              {/* What it skipped, as prominently as what it issued. */}
              {t('invoices.run.result', {
                values: {
                  issued: String(run.issued),
                  drafted: String(run.drafted),
                  skipped: String(run.skipped),
                },
              })}
            </span>
          ) : null}
        </div>
      ) : null}

      {run && run.outcomes.some((outcome) => outcome.skipped) ? (
        <Panel data-invoices-skipped>
          <ul className="flex flex-col gap-1 text-dense text-ink-600">
            {run.outcomes
              .filter((outcome) => outcome.skipped)
              .map((outcome) => (
                <li key={`${outcome.subscriptionId}-${outcome.periodStart ?? 'none'}`}>{outcome.skipped}</li>
              ))}
          </ul>
        </Panel>
      ) : null}

      {drafts > 0 ? (
        <p className="text-dense text-warning-text" data-invoices-draft-count>
          {t('invoices.drafts', { values: { count: String(drafts) } })}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState title={t('invoices.empty')} body={t('invoices.empty.help')} />
      ) : (
        <Panel>
          <div className="overflow-x-auto" data-scroll-container>
            <table className="w-full text-dense">
              <thead>
                <tr className="text-start text-meta text-ink-meta">
                  <th className="p-2 text-start">{t('invoices.column.number')}</th>
                  <th className="p-2 text-start">{t('invoices.column.account')}</th>
                  <th className="p-2 text-start">{t('invoices.column.period')}</th>
                  <th className="p-2 text-start">{t('invoices.column.status')}</th>
                  <th className="p-2 text-end">{t('invoices.column.gross')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((invoice: Invoice) => (
                  <tr key={invoice.invoiceId} className="border-t border-line" data-invoice={invoice.invoiceId}>
                    {/* A draft has no number yet, and saying so beats an empty cell. */}
                    <td className="p-2 text-ink">{invoice.number ?? t('invoices.unnumbered')}</td>
                    <td className="p-2 text-ink-600">{invoice.accountName ?? '—'}</td>
                    <td className="p-2 text-ink-meta">
                      {invoice.periodStart && invoice.periodEnd ? `${invoice.periodStart} → ${invoice.periodEnd}` : '—'}
                    </td>
                    <td className="p-2" data-invoice-status={invoice.status}>
                      <span className={MONEY[invoice.status]}>{t(`invoices.status.${invoice.status}`)}</span>
                      {invoice.voidReason ? <span className="ms-2 text-meta text-ink-meta">{invoice.voidReason}</span> : null}
                    </td>
                    <td className="p-2 text-end text-ink">
                      {invoice.gross} {invoice.currency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {recognition.data && recognition.data.months.some((month) => month.recognised !== '0.00') ? (
        <Panel data-invoices-recognition>
          <h2 className="text-body text-ink">{t('recognition.title')}</h2>
          <p className="text-meta text-ink-meta">{t('recognition.help')}</p>
          <div className="mt-2 overflow-x-auto" data-scroll-container>
            <table className="w-full text-dense">
              <thead>
                <tr className="text-meta text-ink-meta">
                  <th className="p-2 text-start">{t('recognition.column.month')}</th>
                  <th className="p-2 text-end">{t('recognition.column.recognised')}</th>
                  <th className="p-2 text-end">{t('recognition.column.deferred')}</th>
                </tr>
              </thead>
              <tbody>
                {recognition.data.months.map((month) => (
                  <tr key={month.period} className="border-t border-line" data-recognition-month={month.period}>
                    <td className="p-2 text-ink-600">{month.period}</td>
                    <td className="p-2 text-end text-ink" data-recognition-earned>
                      {month.recognised}
                    </td>
                    {/* Beside the earned figure, never instead of it: the pair
                        is the point. */}
                    <td className="p-2 text-end text-ink-meta" data-recognition-deferred>
                      {month.deferred}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      {meters.data && meters.data.meters.length > 0 ? (
        <Panel data-invoices-meters>
          <h2 className="text-body text-ink">{t('usage.meters.title')}</h2>
          <p className="text-meta text-ink-meta">{t('usage.meters.help')}</p>
          <ul className="mt-2 flex flex-col gap-1 text-dense text-ink-600">
            {meters.data.meters.map((meter) => (
              <li key={meter.meterId} data-usage-meter={meter.key}>
                <span className="text-ink">{meter.name}</span>
                {' — '}
                {/* In words: "sum" and "max" beside each other are a setting
                    nobody reads, and reading it wrong is an order of
                    magnitude on somebody's bill. */}
                <span data-usage-aggregation={meter.aggregation}>
                  {t(`usage.aggregation.${meter.aggregation}`, { values: { unit: meter.unit } })}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}

function Loading(): JSX.Element {
  return (
    <div data-component="Invoices">
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function Empty(): JSX.Element {
  const t = useT();
  return (
    <div data-component="Invoices">
      <EmptyState title={t('invoices.empty')} body={t('invoices.empty.help')} />
    </div>
  );
}

function Failed(): JSX.Element {
  const t = useT();
  return (
    <div data-component="Invoices">
      <ErrorState title={t('state.error')} body={t('invoices.help')} />
    </div>
  );
}

export const screen = defineScreen({
  id: 'SC-75',
  route: '/revenue/invoices',
  surface: 'page',
  title: 'Invoices',
  features: ['F-080'],
  entitlement: 'F-080',
  reachedBy: [{ kind: 'screen', from: 'SC-45', how: 'Open invoices from the revenue screen' }],
  components: [],
  elements: ['Invoices'],
  states: { empty: Empty, loading: Loading, error: Failed },
  component: InvoicesScreen,
});

export default screen;
